import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { TippyDirective } from "@ngneat/helipopper";
import { ModalService } from "@components/modal/modal.service";
import { NotificationService } from "@app/services/notifications.service";
import { AppCommonModule } from "@components/app.common.module";
import { CommonModule, NgIf } from "@angular/common";
import {
  FinancialProductService
} from "@app/pages/quote/standard-quote/catalogs/financial-product/financial-product.service";
import {
  FinancialSetupModel
} from "@app/pages/quote/standard-quote/catalogs/financial-setup/new-financial-setup/components/model-template/financial-setup.model";
import {
  conditions
} from "@app/pages/quote/standard-quote/catalogs/financial-product/list-financial-product/config-financial-product.model";
import {TranslateModule} from "@ngx-translate/core";
import { forkJoin } from 'rxjs';

type Filter = {
  classification: boolean | null,
  name: string | null,
  modelType: string | null,
  brandId: number | null,
  subBrandId: string | null,
  year: number | null,
}

type FilterEmiter = {
  models: FinancialSetupModel[],
  filter: any
}


@Component({
  selector: 'app-model-template',
  standalone: true,
  imports: [
    CommonModule, TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    TippyDirective,
    AppCommonModule,
    NgIf
  ],
  templateUrl: './model-template.component.html',
  styleUrl: './model-template.component.css',
})
export class ModelTemplateComponent implements OnInit {

  @Output() save = new EventEmitter<any>();
  @Output() modelSelectedEmit = new EventEmitter<FilterEmiter>();

  @Input() modelType: any;
  @Input() financialSetupModels: FinancialSetupModel[] = [];
  @Input() isEdit: boolean = false;
  @Input() filterModels: any;
  @Input() classification: boolean;



  models: FinancialSetupModel[] = [];
  modelsFiltered: FinancialSetupModel[] = [];
  totalModelsForCurrentQuery = 0;
  years: { value: number; label: string; }[] = [];
  brands: { value: number; label: string; }[] = [];
  subBrands: { value: number; label: string; brandId: number }[] = [];
  subBrandsFiltered: { value: number; label: string; brandId: number }[] = [];
  isLoading: boolean = false;
  reloadSubBrand: boolean = false;
  onlyCheckedModels: boolean = false;
  conditions = conditions
  searchName: any = { placeholder: 'Buscar por nombre', value: '' };
  reset: boolean;
  isSearch: boolean = false;

  params: Filter = {
    classification: null,
    name: null,
    modelType: null,
    brandId: null,
    subBrandId: null,
    year: null,
  };

  constructor(private modalService: ModalService,
    private notificationsService: NotificationService,
    private financialProductService: FinancialProductService,
  ) { }

  ngOnInit(): void {

    this.resetFilter()
    if (this.classification) {
      this.filter(this.classification, 'classification');
      this.search();
    }
    this.params.modelType = this.modelType.value;
    if (this.filterModels) {
      Object.entries(this.filterModels).forEach(([key, value]) => {
        this.filter(value, key)
      })
    }
    this.getYears();
    this.loadBrandsAndSubBrands();
  }


  private loadBrandsAndSubBrands(): void {
    const modelType = this.params.modelType || this.modelType?.value;
    if (!modelType) {
      return;
    }

    const subId =
      this.params.subBrandId != null && this.params.subBrandId !== ''
        ? Number(this.params.subBrandId)
        : null;
    const brandId = this.params.brandId;

    const body = {
      modelType,
      classification: this.params.classification,
      year: this.params.year ?? null,
      brandId,
      subBrandId: Number.isFinite(subId as number) ? subId : null,
    };

    this.financialProductService.getBrandsAndSubBrandsForModelType(body).subscribe({
      next: (response) => {
        if (response.status) {
          const data = response.data || {};
          const rawBrands = (data.brands || []).filter(
            (b: { enabled?: boolean }) => b.enabled !== false
          );
          let rawSub = (data.subBrands || []).filter(
            (s: { enabled?: boolean }) => s.enabled !== false
          );

          this.brands = rawBrands.map((m: { id: number; name?: string; brandCode?: string }) => ({
            value: m.id,
            label: m.name ?? m.brandCode ?? String(m.id),
          }));

          // Sin marca, brand-sub-brand devuelve subBrands vacío: pedir submarcas por cada marca del tipo
          // (misma query por modelType en back) y unir — evita submarcas de otra línea de producto bajo la misma marca.
          const needsSubBrandsPerTypeBrand =
            brandId == null && rawSub.length === 0 && this.brands.length > 0;

          if (needsSubBrandsPerTypeBrand) {
            const baseBody = {
              modelType: body.modelType,
              classification: body.classification,
              year: body.year ?? null,
              subBrandId: null as number | null,
            };
            const requests = this.brands.map((b) =>
              this.financialProductService.getBrandsAndSubBrandsForModelType({
                ...baseBody,
                brandId: b.value,
              })
            );
            forkJoin(requests).subscribe({
              next: (responses) => {
                const byId = new Map<number, unknown>();
                for (const res of responses) {
                  if (!res?.status) continue;
                  const list = ((res.data as { subBrands?: any[] })?.subBrands || []).filter(
                    (s: { enabled?: boolean }) => s.enabled !== false
                  );
                  for (const s of list) {
                    if (s?.id != null && !byId.has(s.id)) {
                      byId.set(s.id, s);
                    }
                  }
                }
                this.applyMappedSubBrands(Array.from(byId.values()));
              },
              error: () => {
                this.notificationsService.showMessage(
                  ['Ha ocurrido un error. Vuelva a intentarlo.'],
                  'error'
                );
              },
            });
            return;
          }

          this.applyMappedSubBrands(rawSub);
        } else {
          this.notificationsService.showMessage(
            ['Ha ocurrido un error. Vuelva a intentarlo.'],
            'error'
          );
        }
      },
      error: () => {
        this.notificationsService.showMessage(
          ['Ha ocurrido un error. Vuelva a intentarlo.'],
          'error'
        );
      },
    });
  }

  /** Mapea submarcas desde brand-sub-brand (`name` o `subBrand` + brand anidado / brandId). */
  private applyMappedSubBrands(rawSub: any[]): void {
    this.subBrands = rawSub.map((m: any) => ({
      value: m.id,
      label: m.name ?? m.subBrand ?? m.subBrandCode ?? String(m.id),
      brandId: m.brand?.id ?? m.brandId,
    }));
    this.applySubBrandsFilteredForUi();
    this.pruneInvalidBrandSubBrandSelection();
  }

  private applySubBrandsFilteredForUi(): void {
    if (this.params.brandId != null) {
      const bid = this.params.brandId;
      this.subBrandsFiltered = this.subBrands.filter((s) => s.brandId === bid);
    } else {
      this.subBrandsFiltered = this.subBrands.length ? [...this.subBrands] : [];
    }
  }

  /** Si cambió año/catálogo y la marca/submarca ya no existen en listas, limpiar params. */
  private pruneInvalidBrandSubBrandSelection(): void {
    const bid = this.params.brandId;
    if (bid != null && !this.brands.some((b) => b.value === bid)) {
      this.params.brandId = null;
      this.params.subBrandId = null;
      this.applySubBrandsFilteredForUi();
      return;
    }
    const sid =
      this.params.subBrandId != null && this.params.subBrandId !== ''
        ? Number(this.params.subBrandId)
        : null;
    if (sid != null && !this.subBrandsFiltered.some((s) => s.value === sid)) {
      this.params.subBrandId = null;
    }
  }

  public checkAllModels(event: any) {
    this.models.forEach((model) => {
      model.isChecked = true;
      if (!this.financialSetupModels.find(item => item.id === model.id)) {
        this.financialSetupModels.push(model);
      }
    });
    this.syncPartialFromVisibleState();
  }

  public uncheckAllModels(event: any) {
    delete this.modelType["partial"];

    this.models.forEach((model) => {
      model.isChecked = false;
    });

    this.models.forEach((model) => {
      const index = this.financialSetupModels.findIndex(
        (item) => item.id === model.id
      );
      if (index !== -1) {
        this.financialSetupModels.splice(index, 1);
      }
    });
  }

  public addModel(event: any, model: FinancialSetupModel) {
    model.isChecked = event.target.checked;

    if (event.target.checked) {
      if (!this.financialSetupModels.some(item => item.id === model.id)) {
        this.financialSetupModels.push({ ...model });
      }
    } else {
      const indexToDelete = this.financialSetupModels.findIndex(item => item.id === model.id);
      if (indexToDelete !== -1) {
        this.financialSetupModels.splice(indexToDelete, 1);
      }
    }
    this.syncPartialFromVisibleState();
  }

  getYears() {
    this.financialProductService.getYears().subscribe({
      next: (response) => {
        if (response.status) {
          this.years = response.data.map((year: any) =>
            ({ value: year, label: year })
          );
        } else {
          this.notificationsService.showMessage(
            ['Ha ocurrido un error. Vuelva a intentarlo.'],
            'error'
          );
        }
      },
      error: (_error) => {
        this.notificationsService.showMessage(
          ['Ha ocurrido un error. Vuelva a intentarlo.'],
          'error'
        );
      },
    });
  }

  getModels() {
    this.isLoading = true;

    if (!this.params.modelType && this.modelType?.value) {
      this.params.modelType = this.modelType.value;
    }

    this.financialProductService.getModelsToFinancialSetup(this.params).subscribe({
      next: (response) => {
        if (response.status) {
          const models: FinancialSetupModel[] = response.data;

          const typeVal = this.modelType?.value;
          const hasStoredForType =
            typeVal &&
            this.financialSetupModels.some((m) => m.modelType === typeVal);
          const implicitAll =
            this.modelType?.partial === false && typeVal && !hasStoredForType;

          models.forEach((model) => {
            const matchedModel = this.financialSetupModels.find(
              (selectedModel) => selectedModel.id === model.id
            );
            model.isChecked = !!matchedModel || !!implicitAll;
          });

          this.totalModelsForCurrentQuery = models.length;
          this.models = models;
          if (this.onlyCheckedModels) {
            this.models = this.models.filter((model) => model.isChecked);
          }

          this.isLoading = false;
        } else {
          this.notificationsService.showMessage(
            ['Ha ocurrido un error. Vuelva a intentarlo.'],
            'error'
          );
          this.isLoading = false;
        }
      },
      error: (_error) => {
        this.isLoading = false;
        this.notificationsService.showMessage(
          ['Ha ocurrido un error. Vuelva a intentarlo.'],
          'error'
        );
      },
    });
  }

  verifyPartialFilter() {
    this.syncPartialFromVisibleState();
  }

  /** Alinea modelType.partial con la selección visible (isChecked) y el total de la consulta. */
  private syncPartialFromVisibleState(): void {
    const visibleTotal = this.models?.length ?? 0;
    const visibleChecked = this.models?.filter((m) => m.isChecked).length ?? 0;
    const fullTotal = this.getModelsSizeForPartialComparison();

    if (visibleTotal === 0 || visibleChecked === 0) {
      delete this.modelType["partial"];
      return;
    }
    if (this.isSearch && visibleTotal < fullTotal) {
      this.modelType["partial"] = true;
      return;
    }
    const allVisibleSelected =
      visibleChecked === visibleTotal && visibleChecked === fullTotal;
    if (allVisibleSelected && !this.hasUserAppliedModelFilters()) {
      this.modelType["partial"] = false;
      return;
    }
    this.modelType["partial"] = true;
  }

  private hasUserAppliedModelFilters(): boolean {
    const p = this.params;
    if (p.name != null && String(p.name).trim() !== '') {
      return true;
    }
    if (p.brandId != null) {
      return true;
    }
    if (p.subBrandId != null) {
      return true;
    }
    if (p.year != null) {
      return true;
    }
    return false;
  }


  private getModelsSizeForPartialComparison(): number {
    if (this.onlyCheckedModels && this.totalModelsForCurrentQuery > 0) {
      return this.totalModelsForCurrentQuery;
    }
    return this.models?.length ?? 0;
  }


  private mergeProductTypeSelectionIntoFinancialSetupModels(typeVal: string): void {
    const currentViewIds = new Set(this.models.map((m) => m.id));
    const byId = new Map<number, FinancialSetupModel>();

    this.financialSetupModels
      .filter((m) => m.modelType === typeVal && !currentViewIds.has(m.id))
      .forEach((m) => byId.set(m.id, { ...m }));

    this.models.forEach((m) => {
      if (m.isChecked) {
        byId.set(m.id, { ...m });
      } else {
        byId.delete(m.id);
      }
    });

    const mergedForType = Array.from(byId.values());
    const other = this.financialSetupModels.filter((m) => m.modelType !== typeVal);
    this.financialSetupModels = [...other, ...mergedForType];
  }

  //add close method
  close() {
    this.modalService.closeModal();
    this.models = [];
  }

  closeAndSave() {
    this.verifyPartialFilter();
    const typeVal = this.modelType?.value;
    if (typeVal) {
      if (this.modelType?.partial === false) {
        this.financialSetupModels = this.financialSetupModels.filter(
          (m) => m.modelType !== typeVal
        );
      } else if (this.modelType?.partial === true) {
        // Unir con selección previa del mismo tipo que no está en la vista actual (p. ej. todo 2026 y luego todo 2025)
        this.mergeProductTypeSelectionIntoFinancialSetupModels(typeVal);
      } else {
        this.financialSetupModels = this.financialSetupModels.filter(
          (m) => m.modelType !== typeVal
        );
      }
    }
    this.save.emit();
    this.modelSelectedEmit.emit({ models: this.financialSetupModels, filter: this.modelType });
    this.close();
  }

  showAmountByFilterModelType(models: any) {
    return models?.filter((m: any) => m.modelType === this.modelType.value).length ?? 0;
  }

  /** Seleccionados en la lista visible (incluye "Todos" lógicos vía isChecked / implicitAll). */
  getVisibleSelectedCount(): number {
    if (!this.models?.length) return 0;
    return this.models.filter((m) => m.isChecked).length;
  }

  showAmount(models: any) {
    return models?.length ?? 0;
  }

  filter(value: any, typeField: string) {
    switch (typeField) {
      case 'name':
        this.params.name = value == '' ? null : value;
        break;
      case 'brandId': {
        const prev = this.params.brandId;
        const n = value === '' || value == null ? NaN : Number(value);
        this.params.brandId = Number.isFinite(n) ? n : null;
        if (prev !== this.params.brandId) {
          this.params.subBrandId = null;
        }
        this.loadBrandsAndSubBrands();
        break;
      }
      case 'subBrandId':
        this.params.subBrandId = value == '' ? null : value;
        break;
      case 'classification':
        this.params.classification = value == '' ? null : value;
        this.loadBrandsAndSubBrands();
        break;
      case 'modelType':
        this.params.modelType = value == '' ? null : value;
        this.loadBrandsAndSubBrands();
        break;
      case 'year':
        this.params.year = value == '' ? null : value;
        this.loadBrandsAndSubBrands();
        break;
    }
  }

  search() {
    if (this.onlyCheckedModels) {
      this.models = this.models.filter((model) =>
        this.financialSetupModels.some(modelCheck => modelCheck.id === model.id) &&
        (model.brandId == this.params.brandId
          || model.subBrand == this.params.subBrandId
          || model.year == this.params.year
          || (this.params.name && model.name.includes(this.params.name)))
      );
      this.isSearch = true;
      //   this.models = this.models.filter((model) => model.isChecked);
    } else {
      this.loadBrandsAndSubBrands();
      this.getModels();
      this.isSearch = true;
    }
  }

  resetFilter() {
    this.params = {
      name: null,
      classification: this.classification,
      modelType: this.modelType.value,
      brandId: null,
      subBrandId: null,
      year: null,
    };
    this.loadBrandsAndSubBrands();
    this.getModels();
  }

  resetFilters() {
    this.params = {
      name: null,
      classification: this.classification,
      modelType: this.modelType.value,
      brandId: null,
      subBrandId: null,
      year: null,
    };
    this.reset = !this.reset;
    this.onlyCheckedModels = false;
    this.loadBrandsAndSubBrands();
    this.getModels();
  }

  public toggleOnlyCheckedModels(event: any) {
    this.onlyCheckedModels = event.target.checked;
    if (this.onlyCheckedModels) {
      this.totalModelsForCurrentQuery = this.models.length;
      this.models = this.models.filter((model) => model.isChecked);
    } else {
      this.getModels();
    }
  }
}
